import { Alert, Box } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { memo, useEffect, useMemo, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import BudgetsFiltersAutocomplete from '@/components/features/BudgetGroupsList/BudgetsFiltersAutocomplete';
import BaseTable from '@/components/shared/Table/BaseTable';
import useBaseTable from '@/components/shared/Table/hooks/useBaseTable';
import { useOrganizationId } from '@/contexts/OrganizationContext';
import type { BudgetGroup } from '@/hooks/useBudgetGroups';
import { useBudgetGroups } from '@/hooks/useBudgetGroups';
import { useTableNavigation } from '@/hooks/useTableNavigation';
import { DEFAULT_PAGE_SIZE, useTablePaginationState } from '@/hooks/useTablePaginationState';

const columnHelper = createColumnHelper<BudgetGroup>();

function BudgetGroupsList() {
  const { organizationId } = useOrganizationId();

  // Define columns
  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        size: 50,
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('isActive', {
        header: 'Status',
        size: 50,
        cell: (info) => {
          const status = info.getValue();
          if (status === undefined || status === null) {
            return '-';
          }
          return status ? 'Active' : 'Inactive';
        },
      }),
    ],
    [],
  );

  // Get pagination state from URL (single source of truth)
  const { pagination, apiPage, setPagination } = useTablePaginationState({
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // Get filter state from URL (single source of truth)
  const filterString = searchParams.get('groups');
  const filterEmails = searchParams.get('emails');

  const groupNames = filterString
    ? filterString
        .split(',')
        .filter(Boolean)
        .map((name) => ({ name }))
    : null;

  const emails = filterEmails
    ? filterEmails
        .split(',')
        .filter(Boolean)
        .map((email) => ({ email }))
    : null;

  // Fetch data with current pagination from URL
  const { budgetGroups, isLoading, error } = useBudgetGroups(apiPage, pagination.pageSize, 'name', groupNames, emails);

  const budgetGroupsList = budgetGroups?.items ?? [];

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const scrollTopParam = searchParams.get('scrollTop');
  const locationState = location.state as { returnSearchParams?: string } | undefined;
  const returnSearchParams = locationState?.returnSearchParams;
  const { navigateToDetail, restoreScrollPosition } = useTableNavigation({
    containerRef: tableContainerRef,
  });

  // Restore URL params from navigation state when coming back from detail page
  useEffect(() => {
    if (returnSearchParams) {
      const restoredParams = new URLSearchParams(returnSearchParams);
      const currentGroups = searchParams.get('groups');
      const currentEmails = searchParams.get('emails');
      const restoredGroups = restoredParams.get('groups');
      const restoredEmails = restoredParams.get('emails');

      if ((restoredGroups && restoredGroups !== currentGroups) || (restoredEmails && restoredEmails !== currentEmails)) {
        setSearchParams(restoredParams, { replace: true });
      }
    }
  }, [returnSearchParams, searchParams, setSearchParams]);

  useEffect(() => {
    if (!isLoading && budgetGroupsList.length > 0) {
      restoreScrollPosition();
    }
  }, [isLoading, budgetGroupsList.length, restoreScrollPosition, scrollTopParam, returnSearchParams]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, pageIndex: newPage }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPagination({ pageIndex: 0, pageSize: newPageSize });
    const params = new URLSearchParams(window.location.search);
    params.delete('scrollTop');
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  };

  const handleRowClick = (budgetGroupId: string, budgetGroup: BudgetGroup) => {
    if (budgetGroupId) {
      navigateToDetail(`/budgets/manage/${budgetGroupId}`, {
        budgetGroup,
      });
    }
  };

  const tableWithData = useBaseTable<BudgetGroup>({
    tableData: budgetGroupsList,
    columns,
    isRowSelectionEnabled: false,
    isColumnVisibilityEnabled: false,
    isColumnOrderEnabled: false,
    hasPagination: true,
    pagination,
    onPaginationChange: setPagination,
    paginationCount: budgetGroups?.totalCount ?? 0,
    onPageChange: handleChangePage,
    onRowsPerPageChange: handleChangeRowsPerPage,
    rowsPerPageOptions: [10, 50, 100],
  });

  if (error) {
    return (
      <Alert
        severity='error'
        sx={{ display: 'flex', alignItems: 'center', fontSize: '14px', lineHeight: '20px' }}
        data-testid='error-loading-budgetGroups'
      >
        Error loading Budgets: {error.message}
      </Alert>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
        <BudgetsFiltersAutocomplete organizationId={organizationId} />
      </Box>
      <BaseTable<BudgetGroup>
        {...tableWithData}
        testId='budgetGroups-table'
        ariaLabel='BudgetGroups list'
        containerRef={tableContainerRef}
        onRowClick={(_, row) => {
          if (row.original.id) {
            handleRowClick(row.original.id?.toString(), row.original);
          }
        }}
        loadingData={{
          loadingMessage: 'Loading Budgets…',
          isLoading: isLoading,
        }}
        noDataMessage='No Budgets found.'
      />
    </>
  );
}

export default memo(BudgetGroupsList);
